import React, { useCallback } from 'react';
import { FormShowTemplate, useFormShowContext } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import {
  TeamShowType,
  useProjectTeamService,
  ProjectSecurityContext,
  CreateProjectTeam,
  ProjectCriticalPeriodsLayout,
  ProjectDataContext,
  ProjectTeamListLayout,
} from 'domain/shared/newProject';
import { useShowContext } from 'framework/show';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { useStaffingProjectContext } from '../context/StaffingProjectContext';
import { omitDeep } from '@apollo/client/utilities/internal';
import StaffingAssignmentsLayout from './StaffingAssignmentsLayout';
import { ProjectTeamHeatmapSection } from 'domain/shared/projectHeatmap';
import { UpcomingChangesLayout } from 'domain/shared/upcomingChanges';

const StaffingTabProjectProvider = ({ children }: { children: React.ReactNode }) => {
  const { project, refetch } = useStaffingProjectContext();
  const value = React.useMemo(
    () =>
      project
        ? {
            data: omitDeep(project, '__typename'),
            refetch: async () => {
              await refetch();
              return project;
            },
          }
        : null,
    [project, refetch],
  );

  if (!value || !project) return null;

  return (
    <ProjectDataContext.Provider value={value}>
      <ProjectSecurityContext permissions={project.accessInfo}>{children}</ProjectSecurityContext>
    </ProjectDataContext.Provider>
  );
};

const StaffingTabContentBody = () => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { isEditMode } = useFormShowContext();
  const { data } = useShowContext<TeamShowType>();
  const { isNeedCreateTeam } = data.getData();

  if (isEditMode && isNeedCreateTeam) {
    return <CreateProjectTeam />;
  }

  const isStaffingAssignmentsEnabled = isFeatureEnabled(Features.STAFFING_ASSIGNMENTS);
  return (
    <>
      {isStaffingAssignmentsEnabled ? <StaffingAssignmentsLayout /> : <ProjectTeamListLayout />}
      {isFeatureEnabled(Features.STAFFING_HEATMAP) && <ProjectTeamHeatmapSection />}
      {isFeatureEnabled(Features.PROJECT_CRITICAL_PERIODS_TEAM) && <ProjectCriticalPeriodsLayout />}
      {isFeatureEnabled(Features.PROJECT_UPCOMING_CHANGES_TEAM) && <UpcomingChangesLayout />}
    </>
  );
};

const StaffingTabContent = () => {
  const { createProjectTeam, updateProjectTeam, getProjectTeamValidationSchema } = useProjectTeamService();
  const { data, project, refetch } = useStaffingProjectContext();

  const submitFormFunc = useCallback(
    async (formData: FormFieldsData) => {
      const requestBody = {
        id: formData.team.id,
        projectId: project.id,
        location: formData.team.location,
        type: formData.team.type,
        expectedStructure: formData.team.expectedStructure,
      };

      const submitFunc = formData.isNeedCreateTeam ? createProjectTeam : updateProjectTeam;
      await submitFunc(requestBody);

      return Promise.resolve();
    },
    [createProjectTeam, updateProjectTeam, project],
  );

  if (!project) {
    return null;
  }

  return (
    <StaffingTabProjectProvider>
      <FormShowTemplate
        isInitialsLoadingPosition
        getValidationSchemaFunc={getProjectTeamValidationSchema}
        submitFormFunc={submitFormFunc}
        afterSubmit={{
          message: 'The team have been successfully updated.',
          execute: async () => {
            await refetch();
          },
        }}
        getDataFunc={() => Promise.resolve(data)}
      >
        <StaffingTabContentBody />
      </FormShowTemplate>
    </StaffingTabProjectProvider>
  );
};

export default StaffingTabContent;
